// src/pages/customer-panel/signatures/SignaturesPage.js
// Customer Panel - Signature Management (Admin: paginated with filters + zone editor, User: paginated pending/signed)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { useDebouncedCallback } from 'use-debounce';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, Alert, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, LinearProgress, Select, MenuItem, FormControl, Tabs, Tab,
    Radio, RadioGroup, FormControlLabel, Snackbar,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import DrawIcon from '@mui/icons-material/Draw';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InputAdornment from '@mui/material/InputAdornment';
import PaginationBar from '../components/PaginationBar';
import PDFZoneEditor from '../components/PDFZoneEditor';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_LIGHT = '#d1fae5';

const STATUS_CONFIG = {
    pending_signature: { label: 'Pending', color: '#f59e0b', bgcolor: '#fef3c7' },
    signed: { label: 'Signed', color: '#3b82f6', bgcolor: '#eff6ff' },
    resign_requested: { label: 'Re-sign', color: '#f97316', bgcolor: '#fff7ed' },
    completed: { label: 'Completed', color: '#10b981', bgcolor: '#d1fae5' },
};

export default function SignaturesPage() {
    const navigate = useNavigate();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'employee';

    const [driveConfigured, setDriveConfigured] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // === ADMIN STATE ===
    const [signatures, setSignatures] = useState([]);
    const [adminPage, setAdminPage] = useState(1);
    const [adminRowsPerPage, setAdminRowsPerPage] = useState(10);
    const [adminTotal, setAdminTotal] = useState(0);
    const [adminTotalPages, setAdminTotalPages] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');
    const [completionFilter, setCompletionFilter] = useState('all');
    const [teamMembers, setTeamMembers] = useState([]);

    // === USER STATE ===
    const [userTab, setUserTab] = useState(0);
    const [pendingDocs, setPendingDocs] = useState([]);
    const [pendingPage, setPendingPage] = useState(1);
    const [pendingTotal, setPendingTotal] = useState(0);
    const [pendingTotalPages, setPendingTotalPages] = useState(1);
    const [signedDocs, setSignedDocs] = useState([]);
    const [signedPage, setSignedPage] = useState(1);
    const [signedTotal, setSignedTotal] = useState(0);
    const [signedTotalPages, setSignedTotalPages] = useState(1);
    const userRowsPerPage = 10;

    // === UPLOAD DIALOG STATE ===
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadStep, setUploadStep] = useState('select'); // 'select' | 'zones'
    const [uploadFile, setUploadFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUserName, setSelectedUserName] = useState('');
    const [additionalName, setAdditionalName] = useState('');
    const [additionalEmail, setAdditionalEmail] = useState('');
    const [zones, setZones] = useState([]);
    const [zoneSigner, setZoneSigner] = useState('primary');
    const [uploading, setUploading] = useState(false);

    // Debounced search
    const debouncedSearch = useDebouncedCallback((val) => { setSearchTerm(val); setAdminPage(1); }, 500);

    // === INIT ===
    useEffect(() => {
        axiosInstance.get('/settings/drive')
            .then(res => setDriveConfigured(!!res.data?.selected_drive))
            .catch(() => setDriveConfigured(false));
        if (isAdmin) {
            axiosInstance.get('/api/users/team-members')
                .then(res => setTeamMembers(res.data?.data || []))
                .catch(() => {});
        }
    }, []);

    // === ADMIN FETCH ===
    const fetchAdminSignatures = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: adminPage, limit: adminRowsPerPage,
                ...(searchTerm && { search: searchTerm }),
                ...(statusFilter !== 'all' && { status: statusFilter }),
                ...(userFilter !== 'all' && { user_id: userFilter }),
                ...(completionFilter !== 'all' && { completed: completionFilter }),
            };
            const res = await axiosInstance.get('/signatures', { params });
            const data = res.data?.data || {};
            setSignatures(data.items || []);
            setAdminTotal(data.total || 0);
            setAdminTotalPages(data.pages || 1);
        } catch (err) {
            setError('Failed to load signature requests.');
        } finally { setLoading(false); }
    }, [adminPage, adminRowsPerPage, searchTerm, statusFilter, userFilter, completionFilter]);

    // === USER FETCH ===
    const fetchUserPending = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/signatures/user', { params: { page: pendingPage, limit: userRowsPerPage } });
            const data = res.data?.data || {};
            setPendingDocs(data.items || []);
            setPendingTotal(data.total || 0);
            setPendingTotalPages(data.pages || 1);
        } catch { setPendingDocs([]); }
    }, [pendingPage]);

    const fetchUserSigned = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/signatures/user/signed', { params: { page: signedPage, limit: userRowsPerPage } });
            const data = res.data?.data || {};
            setSignedDocs(data.items || []);
            setSignedTotal(data.total || 0);
            setSignedTotalPages(data.pages || 1);
        } catch { setSignedDocs([]); }
    }, [signedPage]);

    const fetchUserData = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchUserPending(), fetchUserSigned()]);
        setLoading(false);
    }, [fetchUserPending, fetchUserSigned]);

    useEffect(() => {
        if (isAdmin) fetchAdminSignatures();
        else fetchUserData();
    }, [isAdmin, fetchAdminSignatures, fetchUserData]);

    // === ADMIN ACTIONS ===
    const handleStatusUpdate = async (token, newStatus) => {
        try {
            await axiosInstance.put(`/signatures/${token}/status`, { status: newStatus });
            setSnackbar({ open: true, message: `Status updated to ${newStatus}.`, severity: 'success' });
            fetchAdminSignatures();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update status.', severity: 'error' });
        }
    };

    const handleResetFilters = () => {
        setSearchInput(''); setSearchTerm(''); setStatusFilter('all'); setUserFilter('all'); setCompletionFilter('all'); setAdminPage(1);
    };

    // === UPLOAD ===
    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadFile(file);
        if (file.type === 'application/pdf') {
            setFilePreview(URL.createObjectURL(file));
        } else {
            setFilePreview(null);
        }
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile || !selectedUserId) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('target_user_id', selectedUserId);
            if (additionalName) formData.append('additional_user_name', additionalName);
            if (additionalEmail) formData.append('additional_user_email', additionalEmail);
            if (zones.length > 0) formData.append('signature_zones', JSON.stringify(zones));

            await axiosInstance.post('/signatures/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSnackbar({ open: true, message: 'Document sent for signature.', severity: 'success' });
            handleUploadClose();
            fetchAdminSignatures();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to upload.', severity: 'error' });
        } finally { setUploading(false); }
    };

    const handleUploadClose = () => {
        setUploadOpen(false); setUploadStep('select'); setUploadFile(null);
        if (filePreview) URL.revokeObjectURL(filePreview);
        setFilePreview(null); setSelectedUserId(''); setSelectedUserName('');
        setAdditionalName(''); setAdditionalEmail(''); setZones([]); setZoneSigner('primary');
    };

    // === HELPERS ===
    const formatDate = (d) => {
        if (!d) return '--';
        try { return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); }
        catch { return '--'; }
    };

    const getStatusChip = (status) => {
        const cfg = STATUS_CONFIG[status] || { label: status || 'Unknown', color: '#64748b', bgcolor: '#f1f5f9' };
        return <Chip label={cfg.label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', color: cfg.color, bgcolor: cfg.bgcolor, height: 22 }} />;
    };

    // ======================== ADMIN VIEW ========================
    const renderAdminView = () => (
        <Box>
            {/* Upload button - only shown when drive is configured */}
            {driveConfigured && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setUploadOpen(true)}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px' }}>
                        Upload for Signature
                    </Button>
                </Box>
            )}

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <FilterListIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    <TextField size="small" placeholder="Search..." value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff' } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setAdminPage(1); }}
                            sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px' }}>
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="pending_signature">Pending</MenuItem>
                            <MenuItem value="signed">Signed</MenuItem>
                            <MenuItem value="resign_requested">Re-sign</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={userFilter} onChange={(e) => { setUserFilter(e.target.value); setAdminPage(1); }}
                            sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px' }}>
                            <MenuItem value="all">All Users</MenuItem>
                            {teamMembers.filter(m => m.role !== 'admin').map(m => (
                                <MenuItem key={m.id} value={m.id} sx={{ fontFamily, fontSize: '0.85rem' }}>{m.name || m.email}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={completionFilter} onChange={(e) => { setCompletionFilter(e.target.value); setAdminPage(1); }}
                            sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px' }}>
                            <MenuItem value="all">All Completion</MenuItem>
                            <MenuItem value="yes">Completed</MenuItem>
                            <MenuItem value="no">Not Completed</MenuItem>
                        </Select>
                    </FormControl>
                    <Button size="small" onClick={handleResetFilters} sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT, fontWeight: 600, fontSize: '0.82rem' }}>
                        Reset
                    </Button>
                </Box>
            </Paper>

            {/* Table */}
            {signatures.length === 0 && !loading ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                    <InsertDriveFileIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No signature requests found.</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Document', 'Assigned To', 'Additional Signer', 'Status', 'Date', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {signatures.map((doc) => (
                                <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DescriptionIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {doc.original_filename || 'Document'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#1e293b' }}>{doc.user || doc.user_email || '--'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{doc.additional_signer_name || '--'}</Typography>
                                    </TableCell>
                                    <TableCell>{getStatusChip(doc.status)}</TableCell>
                                    <TableCell><Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{formatDate(doc.created_at)}</Typography></TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Tooltip title="View">
                                                <IconButton size="small" onClick={() => navigate(`/customer-panel/signatures/${doc.signature_token}`)}
                                                    sx={{ color: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_LIGHT } }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {doc.status === 'signed' && (
                                                <>
                                                    <Tooltip title="Mark Complete">
                                                        <IconButton size="small" onClick={() => handleStatusUpdate(doc.signature_token, 'completed')}
                                                            sx={{ color: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_LIGHT } }}>
                                                            <DoneAllIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Request Re-sign">
                                                        <IconButton size="small" onClick={() => handleStatusUpdate(doc.signature_token, 'resign_requested')}
                                                            sx={{ color: '#f97316', '&:hover': { bgcolor: '#fff7ed' } }}>
                                                            <ReplayIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <PaginationBar
                currentPage={adminPage} totalPages={adminTotalPages} totalRows={adminTotal}
                rowsPerPage={adminRowsPerPage} onPageChange={setAdminPage}
                onRowsPerPageChange={(v) => { setAdminRowsPerPage(v); setAdminPage(1); }}
            />
        </Box>
    );

    // ======================== USER VIEW ========================
    const renderUserView = () => {
        const currentDocs = userTab === 0 ? pendingDocs : signedDocs;
        const currentPage = userTab === 0 ? pendingPage : signedPage;
        const currentTotalPages = userTab === 0 ? pendingTotalPages : signedTotalPages;
        const currentTotal = userTab === 0 ? pendingTotal : signedTotal;
        const onPageChange = userTab === 0 ? setPendingPage : setSignedPage;

        return (
            <Box>
                <Tabs value={userTab} onChange={(_, v) => setUserTab(v)}
                    sx={{ mb: 2, '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: GREEN_ACCENT }, '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT } }}>
                    <Tab label={`Pending (${pendingTotal})`} icon={<PendingActionsIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label={`Signed (${signedTotal})`} icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                </Tabs>

                {currentDocs.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        <InsertDriveFileIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>
                            {userTab === 0 ? 'No documents awaiting your signature.' : 'No signed documents yet.'}
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Document', userTab === 0 ? 'Requested By' : 'Status', 'Date', 'Action'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentDocs.map((doc) => (
                                    <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DescriptionIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500 }}>{doc.original_filename || 'Document'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {userTab === 0 ? (
                                                <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{doc.requester || '--'}</Typography>
                                            ) : getStatusChip(doc.status)}
                                        </TableCell>
                                        <TableCell><Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{formatDate(doc.created_at)}</Typography></TableCell>
                                        <TableCell>
                                            <Button size="small" variant={userTab === 0 ? 'contained' : 'outlined'}
                                                startIcon={userTab === 0 ? <DrawIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => navigate(`/customer-panel/signatures/${doc.signature_token}`)}
                                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px',
                                                    ...(userTab === 0 ? { bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } } : { borderColor: GREEN_ACCENT, color: GREEN_ACCENT }) }}>
                                                {userTab === 0 ? 'Sign' : 'View'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <PaginationBar
                    currentPage={currentPage} totalPages={currentTotalPages} totalRows={currentTotal}
                    rowsPerPage={userRowsPerPage} onPageChange={onPageChange} showRowsPerPage={false}
                />
            </Box>
        );
    };

    // ======================== UPLOAD DIALOG ========================
    const renderUploadDialog = () => {
        const isPdf = uploadFile?.type === 'application/pdf';
        const canProceedToZones = isPdf && uploadFile && selectedUserId;
        const primaryZones = zones.filter(z => z.signer === 'primary').length;
        const additionalZones = zones.filter(z => z.signer === 'additional').length;

        return (
            <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth={uploadStep === 'zones' ? 'lg' : 'sm'} fullWidth
                PaperProps={{ sx: { borderRadius: '14px', minHeight: uploadStep === 'zones' ? '80vh' : 'auto' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {uploadStep === 'select' ? 'Upload Document for Signature' : 'Define Signature Areas'}
                    <IconButton size="small" onClick={handleUploadClose}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {uploadStep === 'select' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                            {/* File */}
                            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}
                                sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', py: 1.5, borderColor: '#e2e8f0', color: '#475569' }}>
                                {uploadFile ? uploadFile.name : 'Select PDF or DOCX file'}
                                <input type="file" hidden accept=".pdf,.docx" onChange={handleFileSelected} />
                            </Button>
                            {/* User */}
                            <FormControl fullWidth size="small">
                                <Select value={selectedUserId} displayEmpty
                                    onChange={(e) => {
                                        setSelectedUserId(e.target.value);
                                        const m = teamMembers.find(m => m.id === e.target.value);
                                        setSelectedUserName(m?.name || m?.email || '');
                                    }}
                                    sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px' }}>
                                    <MenuItem value="" disabled sx={{ fontFamily }}>Select primary signer...</MenuItem>
                                    {teamMembers.map(m => (
                                        <MenuItem key={m.id} value={m.id} sx={{ fontFamily, fontSize: '0.85rem' }}>
                                            {m.name || m.email}{m.role ? ` (${m.role})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* Additional signer */}
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>Additional Signer (Optional)</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField size="small" fullWidth placeholder="Name" value={additionalName} onChange={(e) => setAdditionalName(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }} />
                                <TextField size="small" fullWidth placeholder="Email" value={additionalEmail} onChange={(e) => setAdditionalEmail(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }} />
                            </Box>
                        </Box>
                    ) : (
                        /* ZONE EDITOR STEP */
                        <Box sx={{ display: 'flex', gap: 2, minHeight: '60vh' }}>
                            <Box sx={{ flex: 4 }}>
                                <PDFZoneEditor fileUrl={filePreview} zones={zones} onZonesChange={setZones} zoneSigner={zoneSigner} />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>Create Zone For:</Typography>
                                    <RadioGroup value={zoneSigner} onChange={(e) => setZoneSigner(e.target.value)}>
                                        <FormControlLabel value="primary" label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Primary User</Typography>}
                                            control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#3b82f6' } }} />} />
                                        {additionalName && (
                                            <FormControlLabel value="additional" label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Additional User</Typography>}
                                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8b5cf6' } }} />} />
                                        )}
                                    </RadioGroup>
                                </Paper>
                                <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>Primary: <strong>{selectedUserName}</strong></Typography>
                                    {additionalName && <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>Additional: <strong>{additionalName}</strong></Typography>}
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8', mt: 1 }}>
                                        Zones: {primaryZones} primary{additionalName ? `, ${additionalZones} additional` : ''}
                                    </Typography>
                                </Paper>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                    Click and drag on the PDF to create signature zones. Minimum 20x20px.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    {uploadStep === 'zones' && (
                        <Button onClick={() => setUploadStep('select')} sx={{ fontFamily, textTransform: 'none', color: '#64748b', mr: 'auto' }}>Back</Button>
                    )}
                    <Button onClick={handleUploadClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    {uploadStep === 'select' ? (
                        canProceedToZones ? (
                            <Button variant="contained" onClick={() => setUploadStep('zones')}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                                Next: Define Areas
                            </Button>
                        ) : (
                            <Button variant="contained" onClick={handleUploadSubmit}
                                disabled={!uploadFile || !selectedUserId || uploading}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                                {uploading ? <CircularProgress size={20} color="inherit" /> : 'Send for Signature'}
                            </Button>
                        )
                    ) : (
                        <Button variant="contained" onClick={handleUploadSubmit}
                            disabled={zones.length === 0 || uploading}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                            {uploading ? <CircularProgress size={20} color="inherit" /> : `Send with ${zones.length} Zone(s)`}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    };

    // ======================== MAIN RENDER ========================
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Paper elevation={0} sx={{ p: 2, px: 3, mb: 3, borderRadius: '16px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DrawIcon sx={{ fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Signatures</Typography>
                        <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>{isAdmin ? 'Manage signature requests' : 'View and sign documents'}</Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={isAdmin ? fetchAdminSignatures : fetchUserData} disabled={loading} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
                </Tooltip>
            </Paper>

            {driveConfigured === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : driveConfigured === false ? (
                <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #fbbf24', bgcolor: '#fffbeb', textAlign: 'center' }}>
                    <WarningAmberIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#92400e', mb: 1 }}>Central Drive Setup Required</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#78350f', mb: 2 }}>
                        A cloud storage drive (Google Drive or OneDrive) must be configured before signatures can be managed.
                        {isAdmin ? ' Please go to Settings to connect and configure your cloud storage.' : ' Please contact your administrator to set up cloud storage.'}
                    </Typography>
                    {isAdmin && (
                        <Button variant="contained" onClick={() => navigate('/customer-panel/settings')}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, borderRadius: '10px' }}>
                            Go to Settings
                        </Button>
                    )}
                </Paper>
            ) : (
                <>
                    {error && <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
                    ) : isAdmin ? renderAdminView() : renderUserView()}

                    {renderUploadDialog()}
                </>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
